// 인앱 브라우저(카카오톡·인스타·페북·네이버·라인·밴드 등) 대응(c014). 판별·주소는 src/shared/inAppBrowser.ts(순수 모듈).
// - 카카오톡: 불러오자마자 외부 브라우저로 넘긴다(kakaotalk://web/openExternal). 라인: openExternalBrowser=1로 다시 불러온다.
// - 안드로이드 다른 웹뷰: 첫 탭에서 한 번 Chrome 인텐트를 시도하고, 위쪽 가장자리 띠에 「Chrome으로 열기」를 둔다.
// - iOS 다른 웹뷰: 띠에 「⋯ 메뉴 → Safari로 열기」 안내와 링크 복사를 둔다(클립보드가 막히면 주소 칸을 띄워 선택해 둔다).
// 넘기기는 세션마다 한 번만 자동으로 한다(sessionStorage, 막히면 메모리). 넘기지 못해도 게임은 웹뷰 안에서 그대로 돈다.
// 띠는 타이틀·마을에서만 보이고(던전 전투·창이 열린 동안 숨김) 닫을 수 있다. 플레이 영역을 가리지 않게 게임 화면 위쪽 가장자리 가운데에 얇게 둔다.
// 위쪽 빈 띠가 버튼보다 좁은 폰(568×320 등)은 아이콘 칩을 빈 모퉁이에 둔다(c015, inAppBannerLayout.ts).
import { appLabel, bannerKind, chromeIntentUrl, detectInApp, escapePlan, kakaoExternalUrl, type BannerKind, type InAppInfo } from '../shared/inAppBrowser';
import { isAppChannel } from '../shared/appChannel';
import { Scene, type GameView } from '../shared/types';
import { C, FONT, el } from './dom';
import { viewH, viewRect, viewRotated, viewW } from '../shared/viewport';
import { CHIP, copyFieldCss, copyFieldPlace, inAppBannerPlace, type Box } from './inAppBannerLayout';

const TRIED_KEY = 'dungeonhero.extTried';
const DISMISS_KEY = 'dungeonhero.inappBannerOff';

interface NavWindow {
  /** QA 전용 끼움: 있으면 실제로 이동하지 않고 이 함수만 부른다(Playwright가 addInitScript로 넣는다) */
  __dhNavHook?: (url: string) => void;
  /** 외부 브라우저로 넘기려 한 주소 기록(진단·QA) */
  __dhExternalNav?: string[];
  __dhInApp?: InAppInfo & { banner: BannerKind };
}

const memory = new Map<string, string>();
function sessionGet(k: string): string | null {
  try { const v = sessionStorage.getItem(k); if (v !== null) return v; } catch { /* 웹뷰 저장소 거부 */ }
  return memory.get(k) ?? null;
}
function sessionSet(k: string, v: string): void {
  memory.set(k, v);
  try { sessionStorage.setItem(k, v); } catch { /* 메모리만 */ }
}

/** 외부 앱·브라우저 주소로 이동한다(QA 끼움이 있으면 기록만) */
export function navigateExternal(url: string): void {
  const w = window as unknown as NavWindow;
  (w.__dhExternalNav ??= []).push(url);
  if (typeof w.__dhNavHook === 'function') { w.__dhNavHook(url); return; }
  try { window.location.href = url; } catch { /* 웹뷰가 막음: 띠 안내로 넘어간다 */ }
}

let info: InAppInfo = { app: null, os: 'other' };

/**
 * 부팅 맨 앞에서 한 번 부른다. 인앱이면 계획대로 넘기기를 시작한다. ?inapp=0이면 아무것도 하지 않는다(?inapp=1은 판별만 강제하지 않음 — UA로 판별).
 * 돌려준 정보는 띠가 쓴다.
 */
export function startInAppEscape(): InAppInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return info;
  const params = new URLSearchParams(location.search);
  if (params.get('inapp') === '0') return info;
  // 원스토어 TWA 앱 안(Custom Tabs/WebView 폴백으로 "; wv)"가 붙어도)에서는 외부 브라우저 탈출·배너를 하지 않는다
  if (isAppChannel()) return info;
  const nav = navigator as Navigator & { standalone?: boolean };
  info = detectInApp(nav.userAgent || '', { maxTouchPoints: nav.maxTouchPoints || 0, standalone: nav.standalone === true });
  (window as unknown as NavWindow).__dhInApp = { ...info, banner: bannerKind(info) };
  if (!info.app) return info;
  const plan = escapePlan(info, location.href, sessionGet(TRIED_KEY) === '1');
  if (plan.kind === 'auto') {
    sessionSet(TRIED_KEY, '1');
    navigateExternal(plan.url);
  } else if (plan.kind === 'firstTap') {
    // 인트로는 pointer 계열을 창 캡처 단계에서 삼키지만 click은 흘려 보낸다: click 캡처로 첫 탭을 받는다
    const once = (): void => {
      window.removeEventListener('click', once, true);
      if (sessionGet(TRIED_KEY) === '1') return;
      sessionSet(TRIED_KEY, '1');
      navigateExternal(plan.url);
    };
    window.addEventListener('click', once, true);
  }
  return info;
}

const BTN = 'pointer-events:auto;cursor:pointer;flex:0 0 auto;min-height:44px;min-width:44px;padding:0 12px;border-radius:22px;white-space:nowrap;font:800 12px ' + FONT + ';' +
  'border:1.5px solid ' + C.gold + ';background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;-webkit-tap-highlight-color:transparent;';
const BTN2 = BTN.replace('background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;', 'background:rgba(16,22,42,.9);color:' + C.gold + ';');

/** 위쪽 가장자리 안내 띠. 인앱이 아니면 DOM을 만들지 않는다 */
export class InAppBanner {
  private readonly root: HTMLDivElement | null = null;
  private shown = false;
  private dismissed = false;
  private copyField: HTMLInputElement | null = null;
  private text: HTMLSpanElement | null = null;
  private frame = 0;
  private kPlace = '';
  private readonly kind: BannerKind = null;
  /** 주 버튼과 그 글자(띠) · 아이콘(칩, c015) */
  private main: HTMLButtonElement | null = null;
  private mainLabel = '';
  private mainIcon = '';
  private chip = false;

  constructor(parent: HTMLElement, private readonly view: GameView, src: InAppInfo = info) {
    const kind = bannerKind(src);
    if (!kind || new URLSearchParams(location.search).get('inapp') === '0') return;
    this.dismissed = sessionGet(DISMISS_KEY) === '1';
    // 크기: 최대 360px·게임 화면 너비 40%. 자리는 place()가 위쪽 왼편 영웅 체력 묶음과 오른편 지역 이름·미니맵 묶음 사이 빈 띠 가운데로 잡는다
    const r = el('div', 'position:absolute;left:50%;top:max(4px,var(--dh-st,0px));transform:translateX(-50%);display:none;align-items:center;gap:6px;' +
      'box-sizing:border-box;width:max-content;max-width:min(360px,calc(var(--dh-w,100vw)*.4));overflow:hidden;min-height:48px;padding:2px 2px 2px 12px;border-radius:20px;' +
      'background:rgba(10,14,28,.86);border:1px solid rgba(242,193,78,.6);box-shadow:0 2px 8px rgba(0,0,0,.5);pointer-events:auto;' +
      'font:700 11px/1.25 ' + FONT + ';color:#e8ecf8;word-break:keep-all;z-index:30;', parent);
    r.dataset.testid = 'inapp-banner';
    r.dataset.kind = kind;
    const name = appLabel(src.app ?? 'webview');
    const text = el('span', 'flex:1 1 auto;min-width:0;', r);
    this.text = text;
    this.kind = kind;
    if (kind === 'chrome') {
      text.textContent = name + ' 안에서는 화면·소리가 불안정할 수 있어요';
      const b = el('button', BTN, r, 'Chrome으로 열기');
      b.dataset.testid = 'inapp-open-chrome';
      b.addEventListener('click', () => navigateExternal(chromeIntentUrl(location.href)));
      this.main = b; this.mainLabel = 'Chrome으로 열기'; this.mainIcon = '↗';
    } else if (kind === 'kakao') {
      text.textContent = '카카오톡 안에서는 화면·소리가 불안정할 수 있어요';
      const b = el('button', BTN, r, '외부 브라우저로 열기');
      b.dataset.testid = 'inapp-open-external';
      b.addEventListener('click', () => navigateExternal(kakaoExternalUrl(location.href)));
      this.main = b; this.mainLabel = '외부 브라우저로 열기'; this.mainIcon = '↗';
    } else {
      // iOS는 웹뷰 밖으로 내보낼 방법이 없다(앱이 막는다): 메뉴 위치를 알려 주고 주소를 복사하게 한다
      text.textContent = '⋯ 메뉴 → ‘Safari로 열기’가 더 매끄러워요';
      const c = el('button', BTN, r, '링크 복사');
      c.dataset.testid = 'inapp-copy-link';
      c.addEventListener('click', () => { void this.copyLink(c); });
      this.main = c; this.mainLabel = '링크 복사'; this.mainIcon = '⧉';
    }
    const x = el('button', BTN2 + 'min-width:44px;padding:0;border-radius:50%;font-size:18px;', r, '×');
    x.dataset.testid = 'inapp-dismiss';
    x.setAttribute('aria-label', '안내 닫기');
    x.addEventListener('click', () => { this.dismissed = true; sessionSet(DISMISS_KEY, '1'); this.apply(false); });
    this.root = r;
    this.home = parent;
  }

  private home: HTMLElement | null = null;
  private outside = false;
  /**
   * 세로 폰(#app이 90도 돌아간 상태): 배너를 #app 밖(body 직속)의 실제 화면 아래 가장자리 얇은 띠로 옮긴다.
   * 회전되지 않아 사람이 읽는 방향이고, 타이틀 시작 버튼·로고·주요 단추를 가리지 않는다. 위쪽은 회전 안내띠(rotateHint)가 쓰므로 겹치지 않는다(RG-003·RG-001).
   * 이 모드는 타이틀에서만 보인다(마을의 오른쪽 조작 영역을 덮지 않게). 가로·데스크톱은 기존 게임 안 배치.
   */
  private setOutside(on: boolean): void {
    const r = this.root, home = this.home;
    if (!r || !home || on === this.outside) return;
    this.outside = on;
    r.dataset.place = on ? 'bottom-edge' : 'in-game';
    this.kPlace = '';
    if (on) {
      document.body.appendChild(r);
      r.style.cssText += ';position:fixed;left:50%;top:auto;bottom:calc(env(safe-area-inset-bottom,0px) + 6px);transform:translateX(-50%);max-width:calc(100vw - 16px);z-index:1999999999;';
      if (this.text) this.text.style.display = '';
      this.setChip(false);
    } else {
      home.appendChild(r);
      r.style.position = 'absolute'; r.style.bottom = 'auto'; r.style.zIndex = '30';
      r.style.top = 'max(4px,var(--dh-st,0px))';
    }
  }

  /** 클립보드 쓰기가 막힌 웹뷰는 주소 칸을 띄워 선택해 둔다(길게 눌러 복사) */
  private async copyLink(btn: HTMLButtonElement): Promise<void> {
    const href = location.href;
    try {
      await navigator.clipboard.writeText(href);
      btn.textContent = '복사됨';
      return;
    } catch { /* 아래 대체 */ }
    if (!this.copyField && this.root) {
      // 글자 16px: iOS가 작은 글자 입력 칸 포커스 때 확대·이동하지 않게(c015)
      const f = el('input', copyFieldCss(FONT, C.gold), this.root);
      f.readOnly = true;
      f.value = href;
      f.dataset.testid = 'inapp-link-field';
      this.copyField = f;
      this.placeField();
    }
    const f = this.copyField;
    if (!f) return;
    f.focus(); f.select();
    try { f.setSelectionRange(0, href.length); } catch { /* 일부 웹뷰 */ }
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    btn.textContent = ok ? '복사됨' : '길게 눌러 복사';
  }

  private apply(on: boolean): void {
    if (!this.root || on === this.shown) return;
    this.shown = on;
    this.root.style.display = on ? 'flex' : 'none';
    if (this.copyField && this.copyField.parentElement !== this.root) this.copyField.style.display = on ? '' : 'none';
    if (on) { this.kPlace = ''; this.frame = 0; }
  }

  /** 매 프레임: 타이틀·마을(창이 닫혀 있고 일시정지 아님)에서만 보인다. 던전 전투 중에는 숨긴다 */
  update(): void {
    if (!this.root) return;
    const f = this.view.flow;
    const vv = this.view.village;
    const calm = f.scene === Scene.Title || (f.scene === Scene.Village && !f.paused && !vv?.inventoryOpen && !vv?.stageMapOpen && !vv?.questOpen && !(vv?.menuNpc >= 0));
    const rot = viewRotated();
    this.setOutside(rot);
    this.apply((rot ? f.scene === Scene.Title : calm) && !this.dismissed);
    if (this.shown && !this.outside && (this.frame++ % 10) === 0) this.place();
  }

  /** 보이는 HUD 사각형(게임 화면 좌표). 없거나 숨었으면 null */
  private box(elm: Element | null): Box | null {
    if (!elm || elm === this.root || this.root?.contains(elm)) return null;
    const b = viewRect(elm);
    if (!(b.width > 0 && b.height > 0)) return null;
    const cs = getComputedStyle(elm);
    if (cs.display === 'none' || cs.visibility === 'hidden') return null;
    return { left: b.left, top: b.top, right: b.right, bottom: b.bottom };
  }

  /**
   * 위쪽 왼편(영웅 초상·체력 바)과 오른편(지역 이름·미니맵·버튼) 묶음 사이 빈 띠 가운데에 놓는다(게임 화면 좌표).
   * 띠가 좁으면 설명 글자를 접고 버튼만 남긴다. 버튼도 들어가지 않으면(568×320 등) 아이콘 칩을 빈 모퉁이에 둔다(c015 —
   * 이전에는 화면 전체 폭 가운데로 넓혀 일시정지·지역 HUD를 덮었다). HUD가 없는 타이틀은 화면 가운데. 자리 규칙은 inAppBannerLayout.ts
   */
  private place(): void {
    const r = this.root!;
    const W = viewW(), H = viewH();
    const tl = this.box(document.querySelector('[data-testid="hud-top-left"]'));
    const tr = this.box(document.querySelector('[data-testid="hud-top-right"]'));
    const obstacles: Box[] = [];
    for (const o of document.querySelectorAll('[data-testid="pause"],[data-testid="fullscreen"],[data-testid="hud-objective"],[data-testid^="camera-"],[data-testid^="village-"],[data-testid^="touch-"],[data-testid^="joystick-zone"]')) {
      const b = this.box(o);
      if (b) obstacles.push(b);
    }
    const p = inAppBannerPlace(W, H, tl, tr, obstacles, this.kind ?? 'chrome');
    const key = p.mode === 'band' ? 'b' + p.centerX + ',' + p.maxWidth + ',' + p.text : 'c' + p.left + ',' + p.top;
    if (key === this.kPlace) return;
    this.kPlace = key;
    // 주소 칸 자리 계산용(칩 모드): 화면 크기·칩 상자·피할 HUD
    this.fieldW = W; this.fieldH = H;
    this.fieldAvoid = [...(tl ? [tl] : []), ...(tr ? [tr] : []), ...obstacles];
    this.chipBox = p.mode === 'chip' ? { left: p.left, top: p.top, right: p.left + CHIP.w, bottom: p.top + CHIP.h } : null;
    this.setChip(p.mode === 'chip');
    if (p.mode === 'band') {
      r.style.left = p.centerX + 'px';
      r.style.top = 'max(4px,var(--dh-st,0px))';
      r.style.transform = 'translateX(-50%)';
      r.style.maxWidth = p.maxWidth + 'px';
      if (this.text) this.text.style.display = p.text ? '' : 'none';
    } else {
      r.style.left = p.left + 'px';
      r.style.top = p.top + 'px';
      r.style.transform = 'none';
      r.style.maxWidth = 'none';
      if (this.text) this.text.style.display = 'none';
    }
    this.placeField();
  }

  private fieldW = 0;
  private fieldH = 0;
  private fieldAvoid: Box[] = [];
  private chipBox: Box | null = null;
  /**
   * 링크 복사 대체 주소 칸 자리(PR #157 리뷰 P2): 띠 모드는 띠 안(버튼 옆), 칩 모드는 칩 밖 따로 — 화면 안·HUD와 겹치지 않는 자리(copyFieldPlace).
   * 칩 안에 넣으면 칩이 늘어나 화면 밖·HUD 위로 나갔다
   */
  private placeField(): void {
    const f = this.copyField, r = this.root;
    if (!f || !r) return;
    if (this.chipBox && r.parentElement) {
      const b = copyFieldPlace(this.fieldW, this.fieldH, this.chipBox, this.fieldAvoid);
      if (f.parentElement !== r.parentElement) r.parentElement.appendChild(f);
      f.style.position = 'absolute'; f.style.zIndex = '30';
      f.style.left = b.left + 'px'; f.style.top = b.top + 'px'; f.style.width = (b.right - b.left) + 'px'; f.style.flex = 'none';
    } else {
      if (f.parentElement !== r) r.appendChild(f);
      f.style.position = ''; f.style.zIndex = ''; f.style.left = ''; f.style.top = ''; f.style.width = ''; f.style.flex = '';
    }
    f.style.display = this.shown ? '' : 'none';
  }

  /** 칩: 설명 글자를 숨기고 주 버튼을 아이콘만으로(접근성 이름은 원래 글자) */
  private setChip(on: boolean): void {
    if (on === this.chip || !this.root) return;
    this.chip = on;
    this.root.dataset.mode = on ? 'chip' : 'band';
    this.root.style.padding = on ? '2px' : '2px 2px 2px 12px';
    const b = this.main;
    if (!b) return;
    if (on) {
      if (b.textContent !== '복사됨' && b.textContent !== '길게 눌러 복사') b.textContent = this.mainIcon;
      b.setAttribute('aria-label', this.mainLabel);
      b.style.minWidth = '44px'; b.style.padding = '0'; b.style.fontSize = '16px';
    } else {
      if (b.textContent === this.mainIcon) b.textContent = this.mainLabel;
      b.removeAttribute('aria-label');
      b.style.minWidth = '44px'; b.style.padding = '0 12px'; b.style.fontSize = '';
    }
  }
}
